import { type FC, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { useQuery } from "@apollo/client/react";

import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  DashboardProjectsListDocument,
  EvaluationTypeEnum,
  InboxStatusCountsDocument,
  MeDocument,
  ProjectSort,
  UsersDocument,
} from "@app_schema";

import { useDebounce } from "@application/hooks/use_debounce";
import { useToggle } from "@application/hooks/use_toggle";

import { track } from "@application/utilities/track";

import { Button } from "@styled/button";
import { CaseList } from "@styled/case_card";
import { Page } from "@styled/page";
import { Pagination } from "@styled/pagination";

import { GuidedListEmptyState } from "./guided_list_empty_state";
import { ListNoResults, type NoResultsKind } from "./list_no_results";
import { NewProjectModal } from "./new_project_modal";
import { ProjectFilters } from "./project_filters";
import { ProjectInboxCard } from "./project_inbox_card";
import { type SummaryCategory } from "./summary_count_bar";

// Single status filter: "all" / a category / "archived". Archived folds the
// old tabs into a segment; selecting it switches to the archived view.
export type StatusSegment = "all" | SummaryCategory | "archived";

// The rail writes `?status=`, but a hand-typed/stale URL could carry anything.
// Validate against the known set so a junk value can't reach the query
// variables — fall back to the unfiltered "all" view.
const STATUS_SEGMENTS: StatusSegment[] = [
  "all",
  "needsUpload",
  "processing",
  "readyForReview",
  "archived",
];

export const DashboardProjectsList: FC = () => {
  const [showNewModal, onToggleNewModal] = useToggle();

  // The status filter now lives in the layout's Cases rail; it drives the list
  // through the `?status=` URL param. Reset to page 1 whenever it changes.
  const [params, setParams] = useSearchParams();
  const rawStatus = params.get("status");
  const status: StatusSegment = STATUS_SEGMENTS.includes(
    rawStatus as StatusSegment,
  )
    ? (rawStatus as StatusSegment)
    : "all";
  const [page, setPage] = useState(1);

  // `?new=1` opens the create modal so an external link can land on it — the
  // Drive add-on uses this. Drop the param once consumed so a refresh or a
  // back-navigation doesn't reopen it.
  useEffect(() => {
    if (params.get("new") !== "1") return;

    onToggleNewModal(true);

    const next = new URLSearchParams(params);
    next.delete("new");
    setParams(next, { replace: true });
  }, [params, setParams, onToggleNewModal]);

  // Filter state
  const [query, setQuery] = useState<string | undefined>();
  const [mine, setMine] = useState<boolean | undefined>();
  const [sort, setSort] = useState<ProjectSort>(ProjectSort.CreatedNewest);
  const [assigneeId, setAssigneeId] = useState("");
  const [evaluationType, setEvaluationType] = useState<EvaluationTypeEnum | "">(
    "",
  );

  const debouncedQuery = useDebounce(query);

  // Every filter input, not just the two: an empty result now hides the pager,
  // so a stale page is a dead end rather than something you can click out of.
  useEffect(() => {
    setPage(1);
  }, [status, evaluationType, debouncedQuery, mine, assigneeId]);

  // Category filtering is server-side: pass the selected category segment so
  // the list returns server-filtered, paginated rows (counts then match).
  const category =
    status === "all" || status === "archived" ? undefined : status;

  const { data, previousData, loading } = useQuery(
    DashboardProjectsListDocument,
    {
      variables: {
        query: debouncedQuery,
        mine,
        sort,
        userId: assigneeId || undefined,
        archived: status === "archived",
        category,
        evaluationType: evaluationType || undefined,
        page,
      },
    },
  );

  // Stale-while-revalidate: a status/filter change refetches the combined
  // counts+list query, so fall back to the prior result while it's in flight —
  // otherwise data goes undefined and the segmented control collapses to "All"
  // + the list blanks for a beat.
  const current = data ?? previousData;
  const { data: unfilteredData } = useQuery(InboxStatusCountsDocument);
  const { data: meData } = useQuery(MeDocument);
  const clinician = meData?.me.role === "clinician";

  const { data: usersData } = useQuery(UsersDocument, {
    skip: !meData || clinician,
  });

  useEffect(() => {
    track("projects_listed");
  }, []);

  const projects = current?.projects?.entries ?? [];
  const pagination = current?.projects?.pagination;
  const members = usersData?.users ?? [];

  // summaryCategory comes pre-collapsed from the server (complete →
  // readyForReview lives in ProjectStepStateService) — no client derivation.
  const categorizedProjects = useMemo(
    () =>
      projects.map((project) => ({
        project,
        category: (project.summaryCategory ?? "needsUpload") as SummaryCategory,
      })),
    [projects],
  );

  // Ladder step 1, outranking every filter: no cases in ANY status. These counts
  // are the rail's unfiltered ones, so a search matching nothing can't reach it.
  // `all` excludes archived (`where(archived_at: nil)`), so require both.
  // The counts are a SEPARATE query from the list, so absent counts must not
  // read as zero — otherwise a populated account gets the first-run screen
  // whenever this query is slower than the list, or fails outright.
  const unfiltered = unfilteredData?.inboxStatusCounts;
  const showGuidedEmptyState =
    !loading &&
    unfiltered !== undefined &&
    unfiltered.all === 0 &&
    unfiltered.archived === 0;

  // Until the first result lands we don't know which of the two layouts is
  // right. Rendering the populated chrome meanwhile flashes a toolbar and an
  // empty list at precisely the first-run user this exists for.
  const settled = current !== undefined;

  const isEmpty =
    settled &&
    !loading &&
    unfiltered !== undefined &&
    !showGuidedEmptyState &&
    projects.length === 0;

  // Unfiltered, so the copy can never contradict the number beside it — the
  // list's own `inboxStatusCounts` is scoped by search/mine/assignee.
  const bucketCount = unfiltered?.[status];

  // Strict ladder, first match wins. The question each step asks is whether the
  // USER caused the emptiness: if they did, offer the undo; if they didn't, the
  // emptiness is a fact about their work.
  const noResultsKind: NoResultsKind | undefined = !isEmpty
    ? undefined
    : status === "all" && (unfiltered?.all ?? 0) === 0
      ? "archived"
      : (bucketCount ?? 0) === 0
        ? "bucket"
        : "filtered";

  const noResults = noResultsKind !== undefined;

  const onClearFilters = () => {
    setQuery(undefined);
    setMine(undefined);
    setAssigneeId("");
    setEvaluationType("");
    const next = new URLSearchParams(params);
    next.delete("status");
    setParams(next, { replace: true });
  };

  // The list's one solid action, beside the title.
  const newCaseButton = (
    <Button
      type="button"
      color="navy"
      size="md"
      onClick={() => onToggleNewModal(true)}
    >
      <FontAwesomeIcon icon={faPlus} /> Create a case
    </Button>
  );

  return (
    // The guided empty state hides the whole toolbar — nothing to filter, and it
    // carries its own solid "Create case", so one-solid-per-view still holds.
    <Page
      pageTitle="Cases"
      headerRight={
        settled &&
        !showGuidedEmptyState &&
        noResultsKind !== "archived" &&
        newCaseButton
      }
    >
      {/* Both inline states ARE the create form, so `?new=1` must not stack a
          modal on top: a second NewProjectFields duplicates the ids, and the
          modal's labels resolve to the inputs behind the overlay. */}
      {showNewModal &&
        !showGuidedEmptyState &&
        noResultsKind !== "archived" && (
          <NewProjectModal onClose={() => onToggleNewModal(false)} />
        )}

      {/* Status filter lives on the Cases rail; this row is search, assignee,
          type and sort. Create a case sits in the header. */}
      {settled && !showGuidedEmptyState && (
        <ProjectFilters
          query={query}
          onSearch={setQuery}
          mine={mine}
          onMineChange={setMine}
          clinician={clinician ?? false}
          sortValue={sort}
          onSortChange={setSort}
          assigneeId={assigneeId}
          onAssigneeChange={setAssigneeId}
          members={members as Array<{ id: string; name: string }>}
          evaluationType={evaluationType}
          onEvaluationTypeChange={setEvaluationType}
        />
      )}

      {showGuidedEmptyState ? (
        <GuidedListEmptyState />
      ) : noResults ? (
        <ListNoResults
          kind={noResultsKind}
          status={status}
          archivedCount={unfiltered?.archived ?? 0}
          onClear={onClearFilters}
        />
      ) : (
        <CaseList>
          {categorizedProjects.map(({ project, category }) => (
            <ProjectInboxCard
              key={project.id}
              project={project}
              category={category}
            />
          ))}
        </CaseList>
      )}

      {/* Also gated: `data ?? previousData` keeps the pre-delete pager alive, so
          deleting the last case would leave "Page 1 of 1" under the welcome
          heading. Pagination self-guards on pages === 0, but stale data never
          reaches zero. */}
      {!showGuidedEmptyState && !noResults && pagination && (
        <Pagination
          current={pagination.current}
          pages={pagination.pages}
          count={pagination.count}
          onPage={setPage}
        />
      )}
    </Page>
  );
};
